import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { PipelineCard as PipelineCardType } from '@1130-studio/shared'
import { cn, formatDate } from '../../lib/utils'

interface PipelineCardProps {
  card: PipelineCardType
  onClick?: () => void
  isDragging?: boolean
}

const priorityColors = {
  urgent: 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]',
  high: 'bg-accent-orange',
  medium: 'bg-accent-blue',
  low: 'bg-text-tertiary',
}

const priorityAccents = {
  urgent: 'var(--accent-orange)',
  high: 'var(--accent-orange)',
  medium: 'var(--accent-blue)',
  low: 'rgba(255, 255, 255, 0.5)',
}

const sourceLabels: Record<string, string> = {
  website: 'Сайт',
  telegram: 'Telegram',
  manual: 'Вручную',
  referral: 'Реферал',
}

function formatBudget(budget: string | undefined | null): string | null {
  if (!budget) return null
  const num = parseFloat(budget)
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(0)}K`
  }
  return `${num}`
}

function getDeadlineStatus(deadline: Date | string | undefined | null): 'normal' | 'soon' | 'overdue' {
  if (!deadline) return 'normal'
  const date = new Date(deadline)
  const now = new Date()
  const daysUntil = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

  if (daysUntil < 0) return 'overdue'
  if (daysUntil <= 7) return 'soon'
  return 'normal'
}

function getInitials(name: string): string {
  const words = name.split(' ')
  if (words.length >= 2) {
    return words[0][0] + words[1][0]
  }
  return name.slice(0, 2).toUpperCase()
}

export function PipelineCard({ card, onClick, isDragging }: PipelineCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: card.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    '--card-accent': card.type === 'project'
      ? priorityAccents[card.priority as keyof typeof priorityAccents] || priorityAccents.medium
      : 'var(--accent-orange)',
  } as React.CSSProperties

  const isLead = card.type === 'lead'
  const isProject = card.type === 'project'

  const deadlineStatus = isProject ? getDeadlineStatus(card.deadline) : 'normal'
  const budget = isProject ? formatBudget(card.budget) : null
  const clientName = isProject ? (card.client?.companyName || card.client?.contactName) : null

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={cn(
        'group relative glass-card rounded-[14px] p-4 cursor-grab',
        'transition-all duration-200 overflow-hidden',
        'hover:-translate-y-0.5',
        'before:absolute before:top-0 before:left-0 before:right-0 before:h-[3px]',
        'before:bg-[var(--card-accent)] before:opacity-0 before:transition-opacity',
        'hover:before:opacity-100',
        isDragging && 'opacity-50 rotate-[3deg]'
      )}
    >
      {/* Type Badge */}
      <div className="absolute top-3 right-3 flex gap-1">
        <div
          className={cn(
            'px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider',
            isLead && 'bg-accent-orange/20 text-accent-orange',
            isProject && 'bg-accent-blue/20 text-accent-blue'
          )}
        >
          {isLead ? 'Заявка' : 'Проект'}
        </div>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between mb-3 pr-16">
        <span className="text-[15px] font-semibold leading-snug text-white">
          {card.name}
        </span>
        {isProject && card.priority && (
          <div
            className={cn(
              'w-2 h-2 rounded-full flex-shrink-0 mt-1.5',
              priorityColors[card.priority as keyof typeof priorityColors]
            )}
          />
        )}
      </div>

      {/* Lead-specific content */}
      {isLead && (
        <div className="space-y-2 mb-3">
          {/* Contact info */}
          <div className="flex flex-wrap gap-2 text-[12px] text-text-secondary">
            {card.email && (
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                {card.email}
              </span>
            )}
            {card.phone && (
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                {card.phone}
              </span>
            )}
            {card.telegram && (
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3 opacity-60" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.442-.751-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.015 3.333-1.386 4.025-1.627 4.477-1.635.099-.002.321.023.465.141.121.099.155.232.17.324.015.092.034.303.019.467z"/>
                </svg>
                {card.telegram}
              </span>
            )}
          </div>

          {/* Message preview */}
          {card.message && (
            <p className="text-[12px] text-text-tertiary line-clamp-2 italic">
              "{card.message}"
            </p>
          )}
        </div>
      )}

      {/* Project-specific content */}
      {isProject && clientName && (
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 rounded-md bg-white/10 border border-white/10 flex items-center justify-center text-[11px] font-semibold text-text-secondary">
            {getInitials(clientName)}
          </div>
          <span className="text-[13px] text-text-secondary">{clientName}</span>
        </div>
      )}

      {/* Meta Footer */}
      <div className="flex items-center gap-4 pt-3 border-t border-white/10">
        {/* Lead: Source */}
        {isLead && card.source && (
          <div className="flex items-center gap-1.5 text-xs text-text-secondary">
            <svg className="w-3.5 h-3.5 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4M12 8h.01"/>
            </svg>
            {sourceLabels[card.source] || card.source}
          </div>
        )}

        {/* Project: Deadline */}
        {isProject && card.deadline && (
          <div
            className={cn(
              'flex items-center gap-1.5 text-xs',
              deadlineStatus === 'normal' && 'text-text-secondary',
              deadlineStatus === 'soon' && 'text-accent-orange',
              deadlineStatus === 'overdue' && 'text-red-400'
            )}
          >
            <svg className="w-3.5 h-3.5 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {formatDate(card.deadline)}
          </div>
        )}

        {/* Assigned user (for leads) */}
        {isLead && card.assignedUser && (
          <div className="flex items-center gap-1.5 text-xs text-text-secondary ml-auto">
            <div className="w-5 h-5 rounded-full bg-accent-violet/20 flex items-center justify-center text-[10px] font-semibold text-accent-violet">
              {card.assignedUser.username[0].toUpperCase()}
            </div>
          </div>
        )}

        {/* Budget (for projects) */}
        {isProject && budget && (
          <div className="ml-auto font-mono text-xs font-medium text-white bg-white/10 border border-white/10 px-2 py-1 rounded-md">
            {budget} ₽
          </div>
        )}
      </div>
    </div>
  )
}

export function PipelineCardDragOverlay({ card }: { card: PipelineCardType }) {
  const isLead = card.type === 'lead'
  const isProject = card.type === 'project'
  const budget = isProject ? formatBudget(card.budget) : null
  const clientName = isProject ? (card.client?.companyName || card.client?.contactName) : null

  return (
    <div
      style={{
        '--card-accent': card.type === 'project'
          ? priorityAccents[card.priority as keyof typeof priorityAccents] || priorityAccents.medium
          : 'var(--accent-orange)',
      } as React.CSSProperties}
      className="glass-card rounded-[14px] p-4 shadow-[0_4px_24px_rgba(0,0,0,0.4)] rotate-[3deg] cursor-grabbing"
    >
      {/* Type Badge */}
      <div className="absolute top-3 right-3">
        <div
          className={cn(
            'px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider',
            isLead && 'bg-accent-orange/20 text-accent-orange',
            isProject && 'bg-accent-blue/20 text-accent-blue'
          )}
        >
          {isLead ? 'Заявка' : 'Проект'}
        </div>
      </div>

      <div className="flex items-start justify-between mb-3 pr-16">
        <span className="text-[15px] font-semibold leading-snug text-white">
          {card.name}
        </span>
        {isProject && card.priority && (
          <div className={cn('w-2 h-2 rounded-full flex-shrink-0 mt-1.5', priorityColors[card.priority as keyof typeof priorityColors])} />
        )}
      </div>

      {isProject && clientName && (
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 rounded-md bg-white/10 border border-white/10 flex items-center justify-center text-[11px] font-semibold text-text-secondary">
            {getInitials(clientName)}
          </div>
          <span className="text-[13px] text-text-secondary">{clientName}</span>
        </div>
      )}

      <div className="flex items-center gap-4 pt-3 border-t border-white/10">
        {isLead && card.source && (
          <div className="text-xs text-text-secondary">
            {sourceLabels[card.source] || card.source}
          </div>
        )}
        {isProject && card.deadline && (
          <div className="flex items-center gap-1.5 text-xs text-text-secondary">
            <svg className="w-3.5 h-3.5 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {formatDate(card.deadline)}
          </div>
        )}
        {isProject && budget && (
          <div className="ml-auto font-mono text-xs font-medium text-white bg-white/10 border border-white/10 px-2 py-1 rounded-md">
            {budget} ₽
          </div>
        )}
      </div>
    </div>
  )
}
